import { z } from "zod";

import { railAcquisitionSchema, railVisitSchema } from "./rail-moments";

import { buildBranches } from "./rail-branch-segments";
import { onOneLine } from "./rail-one-line";
import { buildStays } from "./rail-stays";
import { rootPlace, specificPlace } from "./rail-places";
import type { Place } from "./schema/place";
import type { Scene } from "./schema/scene";

export const characterStaySchema = z.object({
  characterId: z.string().min(1),
  rootPlaceId: z.string().min(1),
  /** Part inside the site. Omitted only on older bundles. */
  placeId: z.string().min(1).optional(),
  placeLabel: z.string().min(1).optional(),
  startMs: z.number().nonnegative(),
  endMs: z.number().positive(),
  appearanceId: z.string().min(1).optional(),
  /** There, but the reader cannot know yet. Shown only with spoilers shown. */
  secret: z.boolean().optional(),
  /** Player-only: a vehicle, which sits in a row above the circle of characters. */
  vehicle: z.boolean().optional(),
  /** Player-only: the vehicle they were aboard until this stay began, which they step out of. */
  emergesFrom: z.string().min(1).optional(),
});

export const railBranchSchema = z.object({
  id: z.string().min(1),
  rootPlaceId: z.string().min(1),
  label: z.string().min(1),
  startMs: z.number().nonnegative(),
  endMs: z.number().positive(),
  lane: z.number().int(),
  forkFromLane: z.number().int().optional(),
  joinToLane: z.number().int().optional(),
  /** A jump in time: the yarn has split off by the time its first scene starts. */
  leadIn: z.boolean().optional(),
  /** Rejoins after its last scene instead of during it. Not used for flashbacks. */
  leadOut: z.boolean().optional(),
  /** How far back in time it goes, shown where it splits: "Earlier that day". */
  timeLabel: z.string().min(1).optional(),
  /** Player-only: how far each line of this split sits from the main one. */
  laneOffsets: z.record(z.string(), z.number()).optional(),
});

export const railLayoutSchema = z.object({
  branches: z.array(railBranchSchema),
  stays: z.array(characterStaySchema),
  /** Player-only: minors who debut mid-chapter skip host emergence. */
  minorCharacterIds: z.array(z.string().min(1)).optional(),
  /** Player-only: when someone goes to stand beside a vehicle. */
  visits: z.array(railVisitSchema).optional(),
  /** Player-only: when someone takes an animal's DNA, so their markers touch. */
  acquisitions: z.array(railAcquisitionSchema).optional(),
});

export type CharacterStay = z.infer<typeof characterStaySchema>;
export type RailBranch = z.infer<typeof railBranchSchema>;
export type RailLayout = z.infer<typeof railLayoutSchema>;

interface PresentPerson {
  characterId: string;
  appearanceId?: string;
  secret?: boolean;
}

export interface RailBeat {
  startMs: number;
  endMs: number;
  rootPlaceId: string;
  rootLabel: string;
  /** Most specific place. Equals the root when the beat is the site itself. */
  placeId: string;
  placeLabel: string;
  present: PresentPerson[];
  /** Story-time order. A later page with an earlier value is a flashback. */
  inUniverseOrder?: number;
  /** Story clock, on a rail in story order only. A big jump ends a held stay. */
  storyClockMs?: number;
  /** People who go after this beat, rather than waiting until they next appear here. */
  leaves?: string[];
  /** People off the page at the same moment, at a site of their own. */
  elsewhere?: RailBeatGroup[];
}

export interface RailBeatGroup {
  rootPlaceId: string;
  rootLabel: string;
  placeId: string;
  placeLabel: string;
  present: PresentPerson[];
}

/** The scene in story order, without anyone who is only there as it is told. */
export function storyOrderScene(scene: Scene): Scene {
  const { elsewhere, ...rest } = scene;
  const kept = elsewhere?.filter((group) => group.asToldOnly !== true);
  return kept === undefined || kept.length === 0 ? rest : { ...rest, elsewhere: kept };
}

/** Where each person stays, and where the white line forks. Derived, not drafted. */
export function railLayoutForScenes(
  ordered: readonly Scene[],
  places: readonly Place[],
  durationMs: number,
  storyClock: (scene: Scene) => number | undefined = () => undefined,
  options: RailOptions = {},
): RailLayout {
  return buildRailLayout(toBeats(ordered, places, durationMs, storyClock), options);
}

export interface RailOptions {
  /** Only a time difference, such as a flashback, gets its own rail. Others are groups. */
  oneRail?: boolean;
}

export function buildRailLayout(beats: readonly RailBeat[], options: RailOptions = {}): RailLayout {
  if (beats.length === 0) {
    return { branches: [], stays: [] };
  }
  const stays = buildStays(beats);
  if (options.oneRail === true) {
    const lined = onOneLine(beats, stays);
    return { stays: lined, branches: buildBranches(beats, lined) };
  }
  return { stays, branches: buildBranches(beats, stays) };
}

function toBeats(
  ordered: readonly Scene[],
  places: readonly Place[],
  durationMs: number,
  storyClock: (scene: Scene) => number | undefined,
): RailBeat[] {
  let startMs = 0;
  return ordered.map((scene) => {
    const root = rootPlace(scene, places);
    const part = specificPlace(scene, places, root);
    const elsewhere = (scene.elsewhere ?? []).map((group): RailBeatGroup => {
      const at = { ...scene, placeId: group.placeId };
      const groupRoot = rootPlace(at, places);
      const groupPart = specificPlace(at, places, groupRoot);
      return {
        rootPlaceId: groupRoot.id,
        rootLabel: groupRoot.label,
        placeId: groupPart.id,
        placeLabel: groupPart.label,
        present: group.characterIds.map((characterId) => {
          const look = group.looks?.[characterId];
          return look === undefined ? { characterId } : { characterId, appearanceId: look };
        }),
      };
    });
    const beat: RailBeat = {
      startMs,
      endMs: startMs + durationMs,
      rootPlaceId: root.id,
      rootLabel: root.label,
      placeId: part.id,
      placeLabel: part.label,
      present: scene.participants
        .filter((person) => person.role === "present" || person.role === "pov")
        .map((person) => ({
          characterId: person.characterId,
          ...(person.appearanceId === undefined
            ? {}
            : { appearanceId: person.appearanceId }),
          ...(person.secret === true ? { secret: true } : {}),
        })),
      inUniverseOrder: scene.inUniverseOrder,
      storyClockMs: storyClock(scene),
      ...(elsewhere.length === 0 ? {} : { elsewhere }),
      ...(scene.leaves === undefined ? {} : { leaves: scene.leaves }),
    };
    startMs += durationMs;
    return beat;
  });
}
